import { AccountConnectionStatus, IAccount } from '../models/account.models';
import { IGetConversationsParams, IGetSentMessagesParams } from '../models/conversation.models';
import { IInboxFilter } from '../store/smart-inbox.reducers';

export const DEFAULT_CONVERSATION_PAGE_SIZE = 50;

/**
 * The mailbox ids a thread-list request should ask for, dropping disconnected ones. The
 * shared pseudo-inbox (id -1) is always CONNECTED, so it survives as before.
 *
 * An empty result is NOT "ask for everything": the backend reads an empty `mailbox_ids` as
 * every accessible mailbox, so callers must skip the request instead. See
 * `hasNoListableMailbox`.
 */
export function listableMailboxIds(accounts: IAccount[]): number[] {
  return accounts
    .filter((account) => account.connection_status !== AccountConnectionStatus.DISCONNECTED)
    .map((account) => account.id);
}

/**
 * True when there are mailboxes in view but all are disconnected — the one case where no
 * request may be sent. The length check separates that from "accounts not loaded yet",
 * which also yields no ids but must still reach the server.
 */
export function hasNoListableMailbox(accounts: IAccount[]): boolean {
  return accounts.length > 0 && listableMailboxIds(accounts).length === 0;
}

/**
 * Builds the thread-list query params from the inbox filter.
 *
 * Single source of truth on purpose: this used to be duplicated across the
 * conversation list component and two effects, and a filter added to only some
 * of them silently did nothing on the paths that were missed.
 */
export function buildConversationListParams(
  filter: IInboxFilter,
  mailboxIds: number[],
  page = 1,
  limit: number = DEFAULT_CONVERSATION_PAGE_SIZE,
): IGetConversationsParams {
  return {
    mailbox_ids: mailboxIds,
    categories: filter.categories ?? [],
    statuses: filter.statuses ?? [],
    assignee_ids: filter.assigneeIds ?? [],
    // `undefined`, never `[]`. `toHttpParams` does drop an empty array, so this guard is
    // belt-and-braces there — but the backend branches on the raw argument's PRESENCE, so
    // anything that did put a present-but-empty value on the wire would mean "matches
    // nothing" rather than "no filter" and blank the inbox. Copied, not aliased: these
    // params outlive the call and must not track later store updates.
    team_ids: filter.teamIds?.length ? [...filter.teamIds] : undefined,
    search_term: filter.searchTerm?.trim() || undefined,
    from: filter.from || undefined,
    to: filter.to || undefined,
    subject: filter.subject || undefined,
    includes_words: filter.includesWords || undefined,
    excludes_words: filter.excludesWords || undefined,
    date_from: filter.dateFrom || undefined,
    date_to: filter.dateTo || undefined,
    has_attachment: filter.hasAttachment || undefined,
    is_urgent: filter.isUrgent || undefined,
    customer_ids: filter.customerIds?.length ? filter.customerIds : undefined,
    page,
    limit,
  };
}

/** Sent folder equivalent: a flat message list, so only a subset of filters apply. */
export function buildSentMessagesParams(
  filter: IInboxFilter,
  mailboxIds: number[],
  page = 1,
  limit: number = DEFAULT_CONVERSATION_PAGE_SIZE,
): IGetSentMessagesParams {
  return {
    mailbox_ids: mailboxIds,
    search_term: filter.searchTerm?.trim() || undefined,
    date_from: filter.dateFrom || undefined,
    date_to: filter.dateTo || undefined,
    has_attachment: filter.hasAttachment || undefined,
    page,
    limit,
  };
}
